<template>
    <grid
        ref="grid"
        v-if="account"
        :column-defs="columnDefs"
        :row-data="locks"
        :is-auto-height="false"
    >
        <responsive-button
            :icon="['fas', 'user']"
            :clickable="false"
            :responsiveness="false"
            variant="link"
        >{{ accountUsernameTruncated }}</responsive-button>
        <responsive-button
            :icon="['fas', 'gear']"
            @click="handleSettingsClick"
            variant="primary"
        >Settings</responsive-button>
        <responsive-button
            :icon="['fas', 'lock']"
            @click="handleUpdatePasswordClick"
            variant="primary"
        >Update password</responsive-button>
        <responsive-button
            :icon="['fas', 'right-from-bracket']"
            @click="handleDisconnectClick"
            variant="primary"
        >Disconnect</responsive-button>
        <settings-modal-form
            :modal-id="settingsModalID"
        />
        <responsive-button
            :icon="['fas', 'plus']"
            @click="handleAddLockClick"
            variant="primary"
        >Add Lock</responsive-button>
        <disconnect-modal
            title="Disconnect eRentalLock account"
            description="Are you sure you want to disconnect your eRentalLock account? You will not be able to generate lock codes for your reservations any more."
            :modal-id="disconnectModalID"
            :disconnect="disconnect"
            @success-disconnect="load"
        />
        <update-password-modal
            title="Update eRentalLock password"
            :modal-id="updatePasswordModalID"
            :updatePassword="updatePassword"
            @success-save="load">
        </update-password-modal>
        <e-rental-lock-add-lock-modal-form
            :key="addLockModalKey"
            :modal-id="addLockModalID"
            @success-save="handleAddLockSuccess"
        />
    </grid>
    <div v-else-if="busy"></div>
    <div class="mt-1 ml-2" v-else>
        <b-col>
            <b-row>
                <responsive-button
                    :icon="['fas', 'right-to-bracket']"
                    :responsiveness="false"
                    @click="handleLoginClick"
                    variant="primary"
                >Login to eRentalLock</responsive-button>
            </b-row>
        </b-col>
        <e-rental-lock-login-modal-form
            :modal-id="loginModalID"
            @success-save="load"
        />
    </div>
</template>

<script>
    import API from '../../../../api';
    import { truncate } from '../../../../utils/strings';
    import {
        DOOR_LOCKS_E_RENTAL_LOCK_ACCOUNT_ENDPOINT,
        DOOR_LOCKS_E_RENTAL_LOCK_ACCOUNT_DISCONNECT_ENDPOINT,
        DOOR_LOCKS_E_RENTAL_LOCK_ACCOUNT_PASSWORD_ENDPOINT,
        DOOR_LOCKS_E_RENTAL_LOCK_LOCKS_ENDPOINT,
        DOOR_LOCKS_E_RENTAL_LOCK_LOCK_BY_ID_ENDPOINT,
    } from '../../../../endpoints';
    import Grid from '../../../generic/grid/grid.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import DisconnectModal from '../common/disconnect-modal.vue';
    import UpdatePasswordModal from '../common/update-password-modal.vue';
    import SettingsModalForm from '../common/settings-modal-form.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import ERentalLockAddLockModalForm from './e-rental-lock-add-lock-modal-form.vue';
    import ERentalLockLoginModalForm from './e-rental-lock-login-modal-form.vue';

    export default {
        name: 'ERentalLockSettings',
        components: {
            DisconnectModal,
            ERentalLockAddLockModalForm,
            ERentalLockLoginModalForm,
            Grid,
            ResponsiveButton,
            SettingsModalForm,
            UpdatePasswordModal,
            actionsCellRenderer: ActionsCellRenderer,
        },
        computed: {
            accountUsernameTruncated() {
                if (this.account && this.account.username) {
                    return truncate(this.account.username, 20);
                }
                return '';
            }
        },
        data() {
            return {
                busy: false,
                account: null,
                locks: [],
                addLockModalID: 'e-rental-lock-add-lock-modal',
                addLockModalKey: 0,
                disconnectModalID: 'e-rental-lock-disconnect-modal',
                updatePasswordModalID: 'e-rental-lock-update-password-modal',
                settingsModalID: 'e-rental-lock-settings-modal',
                loginModalID: 'e-rental-lock-login-modal',
                columnDefs: [
                    {
                        field: 'lock_id',
                        headerName: 'eRentalLock Lock ID',
                        sort: 'asc',
                    },
                    {
                        field: 'rental_property_name',
                        headerName: 'Lodgix Property',
                    },
                    {
                        headerName: '',
                        maxWidth: 80,
                        minWidth: 80,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            delete: async (lock) => {
                                try {
                                    await API.delete(DOOR_LOCKS_E_RENTAL_LOCK_LOCK_BY_ID_ENDPOINT(lock.id));
                                } catch (error) {
                                    this.errorToast({text: 'Could not delete lock'})
                                    return;
                                }
                                this.refreshLocks();
                            },
                        }
                    },
                ],
            }
        },
        mounted() {
            this.load();
        },
        methods: {
            handleLoginClick() {
                this.$bvModal.show(this.loginModalID);
            },
            handleSettingsClick() {
                this.$bvModal.show(this.settingsModalID);
            },
            handleDisconnectClick() {
                this.$bvModal.show(this.disconnectModalID);
            },
            handleUpdatePasswordClick() {
                this.$bvModal.show(this.updatePasswordModalID);
            },
            handleAddLockClick() {
                this.$bvModal.show(this.addLockModalID);
            },
            handleAddLockSuccess() {
                this.addLockModalKey += 1;
                this.refreshLocks();
            },
            disconnect() {
                return API.post(DOOR_LOCKS_E_RENTAL_LOCK_ACCOUNT_DISCONNECT_ENDPOINT());
            },
            updatePassword(values) {
                return API.post(DOOR_LOCKS_E_RENTAL_LOCK_ACCOUNT_PASSWORD_ENDPOINT(), values);
            },
            async load() {
                this.busy = true;
                this.$parent.$emit('busy', true);

                try {
                    await this.fetchAccount();
                    if (this.account) {
                        await this.fetchLocks();
                    } else {
                        this.locks = [];
                    }
                } finally {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            },
            async fetchAccount() {
                try {
                    const response = await API.get(DOOR_LOCKS_E_RENTAL_LOCK_ACCOUNT_ENDPOINT());
                    this.account = response.data;
                } catch {
                    this.account = null;
                }
            },
            async refreshLocks() {
                this.$parent.$emit('busy', true);

                try {
                    await this.fetchLocks();
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async fetchLocks() {
                try {
                    const response = await API.get(DOOR_LOCKS_E_RENTAL_LOCK_LOCKS_ENDPOINT());
                    this.locks = response.data;
                } catch {               
                    this.errorToast({text: 'Could not load locks'})
                }
            }
        }
    }
</script>